import { makeAutoObservable, runInAction } from 'mobx';
import { PasswordConfig, GeneratedPassword, PasswordPreset } from '../types/password.types';
import { PasswordService } from '../services/password-service';

export class PasswordGeneratorStore {
  config: PasswordConfig = {
    length: 12,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeAmbiguous: false,
    minNumbers: 1,
    minSymbols: 1,
  };

  generatedPasswords: GeneratedPassword[] = [];
  isGenerating = false;
  passwordCount = 1;
  showPasswords = true;
  showConfigModal = true; // Show modal on startup
  useMemorable = false; // New memorable option

  private readonly presets: PasswordPreset[] = [
    {
      name: 'Strong Password',
      description: '16 characters with all types',
      icon: '🔐',
      config: {
        length: 16,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: true,
        includeSymbols: true,
        excludeAmbiguous: true,
        minNumbers: 2,
        minSymbols: 2,
      },
    },
    {
      name: 'Medium Password',
      description: '12 characters, letters and numbers',
      icon: '🔒',
      config: {
        length: 12,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: true,
        includeSymbols: false,
        excludeAmbiguous: false,
        minNumbers: 2,
      },
    },
    {
      name: 'Simple Password',
      description: '8 characters, letters only',
      icon: '🔓',
      config: {
        length: 8,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: false,
        includeSymbols: false,
        excludeAmbiguous: false,
      },
    },
    {
      name: 'PIN Code',
      description: '6 digits only',
      icon: '🔢',
      config: {
        length: 6,
        includeUppercase: false,
        includeLowercase: false,
        includeNumbers: true,
        includeSymbols: false,
        excludeAmbiguous: true,
      },
    },
    {
      name: 'Secure Token',
      description: '32 characters, maximum security',
      icon: '🛡️',
      config: {
        length: 32,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: true,
        includeSymbols: true,
        excludeAmbiguous: true,
        minNumbers: 4,
        minSymbols: 4,
      },
    },
    {
      name: 'Memorable',
      description: '10 characters, no symbols',
      icon: '💭',
      config: {
        length: 10,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: true,
        includeSymbols: false,
        excludeAmbiguous: true,
        minNumbers: 2,
      },
    },
  ];

  constructor() {
    makeAutoObservable(this);
  }

  get allPresets(): PasswordPreset[] {
    return this.presets;
  }

  get isConfigValid(): boolean {
    return (
      this.config.length >= 4 &&
      this.config.length <= 128 &&
      (this.config.includeUppercase ||
        this.config.includeLowercase ||
        this.config.includeNumbers ||
        this.config.includeSymbols)
    );
  }

  get strengthDistribution() {
    const counts = {
      'very-weak': 0,
      weak: 0,
      fair: 0,
      good: 0,
      strong: 0,
    };

    this.generatedPasswords.forEach(password => {
      counts[password.strength]++;
    });

    return counts;
  }

  updateConfig(updates: Partial<PasswordConfig>): void {
    runInAction(() => {
      Object.assign(this.config, updates);
    });
  }

  setPasswordCount(count: number): void {
    runInAction(() => {
      this.passwordCount = Math.max(1, Math.min(10, count));
    });
  }

  togglePasswordVisibility(): void {
    runInAction(() => {
      this.showPasswords = !this.showPasswords;
    });
  }

  applyPreset(preset: PasswordPreset): void {
    runInAction(() => {
      this.config = { ...preset.config };
    });
  }

  async generatePasswords(): Promise<void> {
    if (!this.isConfigValid) {
      throw new Error('Invalid password configuration');
    }

    runInAction(() => {
      this.isGenerating = true;
    });

    try {
      // Simulate async operation for better UX
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const passwords = PasswordService.generateMultiplePasswords(
        this.config,
        this.passwordCount
      );

      runInAction(() => {
        this.generatedPasswords = passwords;
        this.isGenerating = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isGenerating = false;
      });
      throw error;
    }
  }

  regeneratePassword(passwordId: string): void {
    const password = this.generatedPasswords.find(p => p.id === passwordId);
    if (!password) return;

    const newPassword = PasswordService.createGeneratedPassword(password.config);
    
    runInAction(() => {
      const index = this.generatedPasswords.findIndex(p => p.id === passwordId);
      if (index !== -1) {
        this.generatedPasswords[index] = newPassword;
      }
    });
  }

  removePassword(passwordId: string): void {
    runInAction(() => {
      this.generatedPasswords = this.generatedPasswords.filter(p => p.id !== passwordId);
    });
  }

  clearAllPasswords(): void {
    runInAction(() => {
      this.generatedPasswords = [];
    });
  }

  copyPasswordToClipboard(password: string): Promise<boolean> {
    return navigator.clipboard.writeText(password)
      .then(() => true)
      .catch(() => false);
  }

  openConfigModal(): void {
    runInAction(() => {
      this.showConfigModal = true;
    });
  }

  closeConfigModal(): void {
    runInAction(() => {
      this.showConfigModal = false;
    });
  }

  setMemorable(memorable: boolean): void {
    runInAction(() => {
      this.useMemorable = memorable;
      if (memorable) {
        // Apply memorable password settings
        this.config = {
          length: 10,
          includeUppercase: true,
          includeLowercase: true,
          includeNumbers: true,
          includeSymbols: false,
          excludeAmbiguous: true,
          minNumbers: 2,
          minSymbols: 0,
        };
      }
    });
  }
}

export const passwordGeneratorStore = new PasswordGeneratorStore();
